import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

// Aug-2026 (User-Wunsch Handy-Login): mobile Geräte bleiben 7 Tage angemeldet.
const isMobileDevice = () => {
    try { return /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent); } catch { return false; }
};

export const AuthProvider = ({ children }) => {
    const [user, _setUserState] = useState(null); // null = checking, false = anon, object = logged in
    // Jul-2026 (Bugfix): Konto-Wechsel signalisieren, damit modulweite Caches
    // (z. B. useLockedFields) invalidiert werden.
    const setUser = useCallback((u) => {
        _setUserState(u);
        try { window.dispatchEvent(new Event("tw-auth-changed")); } catch { /* noop */ }
    }, []);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // Abgelaufenes 7-Tage-Token nicht erneut wiederherstellen.
            if (e?.response?.status === 401) {
                try { localStorage.removeItem("tw_token_persist"); } catch { /* noop */ }
            }
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    // Jun-2026 (User-Wunsch) — Geräte-Link für mobile Inventur: Öffnet das Handy
    // die Inventur-Seite mit ?devtoken=…, tauschen wir den Token gegen eine
    // vollwertige Session (kein erneutes Login) und entfernen ihn aus der URL.
    const bootstrap = useCallback(async () => {
        // Aug-2026 (User-Wunsch Handy-Login): dauerhaft gespeichertes Token
        // (7-Tage-Login) nach App-/Browser-Neustart wiederherstellen.
        try {
            if (!sessionStorage.getItem("tw_token")) {
                const persisted = localStorage.getItem("tw_token_persist");
                if (persisted) sessionStorage.setItem("tw_token", persisted);
            }
        } catch { /* noop */ }
        try {
            const params = new URLSearchParams(window.location.search);
            const devtoken = params.get("devtoken");
            if (devtoken) {
                const stripDevtoken = () => {
                    params.delete("devtoken");
                    const qs = params.toString();
                    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
                };
                try {
                    const { data } = await api.post("/auth/device-login", { token: devtoken });
                    if (data.token) sessionStorage.setItem("tw_token", data.token);
                    setUser(data.user);
                    stripDevtoken();
                    setLoading(false);
                    return;
                } catch {
                    // Ungültiger/abgelaufener Token → normaler Flow (Login-Seite).
                    stripDevtoken();
                }
            }
        } catch { /* noop */ }
        await fetchMe();
    }, [fetchMe]);

    useEffect(() => {
        bootstrap();
    }, [bootstrap]);

    // Jul-2026 (User-Wunsch) — Tägliche Zwangsabmeldung um 22:00 Uhr: das Backend
    // lehnt ältere Tokens ab 22:00 ohnehin ab; hier melden wir den Nutzer auch im
    // offenen Tab aktiv ab, damit keine Sitzung „optisch" offen bleibt.
    useEffect(() => {
        let timer;
        const schedule = () => {
            const now = new Date();
            const next = new Date(now);
            next.setHours(22, 0, 0, 0);
            if (next <= now) next.setDate(next.getDate() + 1);
            timer = setTimeout(async () => {
                // Handy-Login (7 Tage) ist von der 22-Uhr-Abmeldung ausgenommen.
                const persisted = (() => { try { return localStorage.getItem("tw_token_persist"); } catch { return null; } })();
                if (sessionStorage.getItem("tw_token") && !persisted) {
                    try { await api.post("/auth/logout"); } catch { /* Token evtl. schon ungültig */ }
                    sessionStorage.removeItem("tw_token");
                    setUser(false);
                }
                schedule();
            }, next.getTime() - now.getTime());
        };
        schedule();
        return () => clearTimeout(timer);
    }, []);

    const login = async (email, password, role, totpCode) => {
        // Mai-2026 (User-Wunsch 21 May): Auto-Retry bei ERR_NETWORK (z. B. Corporate-Firewall-Hiccups).
        // Versucht max. 2-mal mit 1.2s Wartezeit. Bei dauerhaftem Netzfehler bleibt die ausführliche
        // Diagnose-Toast erhalten (Code + URL + UA in Konsole).
        const MAX_ATTEMPTS = 3;
        const RETRY_DELAY_MS = 1200;
        const GATEWAY_CODES = [502, 503, 504, 520, 521, 522, 523, 524];
        let lastError = null;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                const remember = isMobileDevice();
                const { data } = await api.post("/auth/login", { email, password, role, totp_code: totpCode || null, remember });
                if (data.token) {
                    sessionStorage.setItem("tw_token", data.token);
                    try {
                        if (remember) localStorage.setItem("tw_token_persist", data.token);
                        else localStorage.removeItem("tw_token_persist");
                    } catch { /* noop */ }
                }
                setUser(data.user);
                return { ok: true, user: data.user };
            } catch (e) {
                lastError = e;
                // Aug-2026 (User-Report „kann sich bei Überlastung nicht einloggen"):
                // Gateway-/Überlastungsfehler (Cloudflare 520/52x, 502-504) ebenfalls
                // automatisch wiederholen — nur echte 4xx sofort anzeigen.
                const st = e.response?.status;
                if (e.response && !GATEWAY_CODES.includes(st)) break;
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt));
                }
            }
        }
        // Fehleraufbereitung (auch bei einem 4xx, in dem Fall ohne Retry).
        const e = lastError;
        const detail = e.response?.data?.detail;
        const totpRequired = typeof detail === "string" && /2FA/i.test(detail);
        // Aug-2026 (User-Wunsch „Fehlermeldung in Klarschrift auf Deutsch"):
        // Cloudflare-/Gateway-Fehler erkennen und verständlich erklären.
        const _raw = e.response?.data;
        const _gateway = GATEWAY_CODES.includes(e.response?.status)
            || (typeof detail === "string" && /cloudflare|origin web server|could not parse/i.test(detail))
            || (typeof _raw === "string" && /cloudflare|origin web server|<html/i.test(_raw));
        let errorMsg;
        if (_gateway) {
            errorMsg = "Der Server ist gerade stark ausgelastet oder startet neu (z. B. durch große Scan-Vorgänge oder ein Update). "
                + "Ihre Zugangsdaten sind NICHT das Problem. Bitte 30–60 Sekunden warten und dann erneut auf ‚Anmelden' klicken.";
        } else if (detail != null) {
            errorMsg = formatApiErrorDetail(detail);
        } else if (!e.response) {
            const code = e.code || "";        // z. B. ERR_NETWORK / ERR_BAD_REQUEST
            const httpStatus = e.request?.status ? ` HTTP ${e.request.status}` : "";
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const tried = `${apiBase}/api/auth/login`;
            const msg = e.message || "Network Error";
            errorMsg = `Verbindungsproblem — bitte Internet-Verbindung prüfen und Seite neu laden (Strg+Shift+R). [${msg}${code ? " · " + code : ""}${httpStatus} · URL: ${tried}]`;
            try {
                // eslint-disable-next-line no-console
                console.error("[twowheels.login] network failure", { msg, code, httpStatus, tried, attempts: MAX_ATTEMPTS, userAgent: navigator.userAgent });
            } catch { /* noop */ }
        } else {
            errorMsg = `Anmeldung fehlgeschlagen (HTTP ${e.response.status}). Bitte Seite neu laden oder kurz später erneut versuchen.`;
        }
        return { ok: false, error: errorMsg, totp_required: totpRequired };
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.token) sessionStorage.setItem("tw_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user, data };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    // Phase 3.56 — used by the Google OAuth callback to skip /auth/login.
    const setSession = (userData, token) => {
        if (token) sessionStorage.setItem("tw_token", token);
        setUser(userData);
    };

    // Jul-2026 (User-Wunsch) — Admin-Impersonation: in ein beliebiges Konto
    // wechseln bzw. wieder zurück zum eigenen Admin-Konto.
    const impersonate = async (userId) => {
        const { data } = await api.post(`/admin/impersonate/${userId}`);
        if (data.token) sessionStorage.setItem("tw_token", data.token);
        sessionStorage.setItem("tw_imp_started", String(Date.now()));
        await fetchMe();
        return data;
    };
    const stopImpersonate = async () => {
        const { data } = await api.post("/admin/stop-impersonate");
        if (data.token) sessionStorage.setItem("tw_token", data.token);
        sessionStorage.removeItem("tw_imp_started");
        await fetchMe();
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.error("Logout request failed", e);
        }
        sessionStorage.removeItem("tw_token");
        try { localStorage.removeItem("tw_token_persist"); } catch { /* noop */ }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, setSession, logout, refresh: fetchMe, impersonate, stopImpersonate }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
